import { useCallback, useEffect, useMemo, useState } from 'react';
import { loadGtag, setDefaultConsent, updateConsent } from '../lib/gtag';

type ConsentChoice = 'accepted' | 'rejected' | 'unknown';

const STORAGE_KEY = 'consent';

const readStoredConsent = (): ConsentChoice => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'accepted' || value === 'rejected') return value;
    return 'unknown';
  } catch {
    return 'unknown';
  }
};

export function useConsent(options?: { gtmId?: string; gaMeasurementId?: string }) {
  const { gtmId, gaMeasurementId } = options || {};
  const [choice, setChoice] = useState<ConsentChoice>(() => readStoredConsent());

  // Initialize consent defaults early
  useEffect(() => {
    // Ensure gtag present if GTM id is provided
    if (gtmId) {
      loadGtag(gtmId);
    }
    setDefaultConsent();
  }, [gtmId]);

  // If we have a stored choice, reflect it to Consent Mode and config
  useEffect(() => {
    if (choice === 'accepted') {
      updateConsent({ 
        ad_storage: 'granted', 
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        personalization_storage: 'granted',
        analytics_storage: 'granted', 
        functionality_storage: 'granted',
        security_storage: 'granted'
      });
      if (gaMeasurementId) {
        // Safe call: gtag wrapper no-ops until loaded
        (window as any).gtag?.('js', new Date());
        (window as any).gtag?.('config', gaMeasurementId);
      }
    } else if (choice === 'rejected') {
      updateConsent({ 
        ad_storage: 'denied', 
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        personalization_storage: 'denied',
        analytics_storage: 'denied', 
        functionality_storage: 'denied',
        security_storage: 'denied'
      });
    }
  }, [choice, gaMeasurementId]);

  const acceptAll = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, 'accepted'); } catch {}
    setChoice('accepted');
    try {
      window.dispatchEvent(new CustomEvent('consent-choice', { detail: { choice: 'accepted' } }));
    } catch {}
  }, []);

  const rejectAll = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, 'rejected'); } catch {}
    setChoice('rejected');
    try {
      window.dispatchEvent(new CustomEvent('consent-choice', { detail: { choice: 'rejected' } }));
    } catch {}
  }, []);

  const shouldShowBanner = useMemo(() => choice === 'unknown', [choice]);

  return { choice, shouldShowBanner, acceptAll, rejectAll };
}


